import type { OTCFixedRateLoanFieldsData } from '@src/types/OTC';
import { isFixedRateLoanProductFields } from '@src/types/OTC/otcGuards';
import { getFieldTitle, getFieldTooltip } from '@src/utils/fieldDefinitionUtils';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import {
  appendTooltipWhenFeatureDisabled,
  OPEN_TERM_SUPPORT_COMING_SOON,
} from '@src/utils/OTC/otcFeatureFlagTooltipAppend';
import {
  DurationInput,
  FormGroup,
  LoanQuoteTerminationTypeEnum,
  NumberInput,
  SearchSelect,
  Stack,
  type Leaves,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { getLoanPaymentCurrencyOptionsForRow } from './loanPaymentCurrencyForRow';
import { isBulletLoanPaymentType } from './loanPaymentType';
import { LoanPeriodFields } from './LoanPeriodFields';
import {
  getDayCountConventionLabel,
  getLoanPaymentCurrencyLabel,
  getTermTypeLabel,
  LOAN_TERM_TYPE_OPTIONS,
  useDayCountConventionOptions,
  type DayCountConventionOption,
  type LoanPaymentCurrencyOption,
  type LoanTermTypeOption,
} from './productLayoutTokens';
import type { OTCFieldsContext } from './types';
import { useLoanOtcTermTypeChange } from './useLoanOtcTermTypeChange';

interface FixedRateLoanProductLayoutProps {
  formData: OTCFixedRateLoanFieldsData;
  disclosureContext: OTCFieldsContext;
  handleFieldChange: (field: Leaves<OTCFixedRateLoanFieldsData>, value: string | boolean | null | undefined) => void;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  requiredFields?: Record<string, boolean>;
  enableLoanOpenTermSupport: boolean;
}

export const FixedRateLoanProductLayout = ({
  formData,
  disclosureContext,
  handleFieldChange,
  errors = {},
  touched = {},
  requiredFields = {},
  enableLoanOpenTermSupport,
}: FixedRateLoanProductLayoutProps) => {
  const dayCountConventionOptions = useDayCountConventionOptions();
  const rowData = disclosureContext.rowData;
  const baseAsset = rowData.baseAsset;
  const collateralAsset = 'collateralAsset' in rowData ? rowData.collateralAsset : null;

  const loanPaymentCurrencyOptions = useMemo((): LoanPaymentCurrencyOption[] => {
    return getLoanPaymentCurrencyOptionsForRow(baseAsset, collateralAsset);
  }, [baseAsset, collateralAsset]);

  const loanPaymentCurrencySelection = useMemo(() => {
    return loanPaymentCurrencyOptions.find(option => option.value === formData.loanPaymentCcy);
  }, [loanPaymentCurrencyOptions, formData.loanPaymentCcy]);

  const handleLoanPaymentCurrencyChange = useCallback(
    (option: LoanPaymentCurrencyOption | undefined) => {
      handleFieldChange('loanPaymentCcy', option?.value);
    },
    [handleFieldChange]
  );

  const termTypeSelection = useMemo((): LoanTermTypeOption | undefined => {
    return LOAN_TERM_TYPE_OPTIONS.find(
      option => option.value === (formData.termType ?? LoanQuoteTerminationTypeEnum.Fixed)
    ) as LoanTermTypeOption | undefined;
  }, [formData.termType]);

  const handleTermTypeChange = useLoanOtcTermTypeChange(handleFieldChange);
  const isBullet = isBulletLoanPaymentType(formData.loanPaymentType);

  const dayCountConventionSelection = useMemo(() => {
    return dayCountConventionOptions.find(option => option.value === formData.dayCountConvention);
  }, [dayCountConventionOptions, formData.dayCountConvention]);

  const handleDayCountConventionChange = useCallback(
    (option: DayCountConventionOption | undefined) => {
      handleFieldChange('dayCountConvention', option?.value);
    },
    [handleFieldChange]
  );

  const isTermTypeFixed =
    (formData.termType ?? LoanQuoteTerminationTypeEnum.Fixed) === LoanQuoteTerminationTypeEnum.Fixed;

  if (!isFixedRateLoanProductFields(formData)) {
    return null;
  }

  return (
    <Stack gap="spacing12" w="100%">
      <FormGroup
        label={getFieldTitle(fixedRateLoanFieldDefinitions.unitLoanAmount)}
        tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.unitLoanAmount)}
        required={requiredFields.unitLoanAmount}
        error={touched.unitLoanAmount ? errors.unitLoanAmount : ''}
        mb="spacing4"
      >
        <NumberInput
          id="unit-loan-amount"
          autoComplete="off"
          min="0"
          value={formData.unitLoanAmount?.toString() || ''}
          onChange={value => handleFieldChange('unitLoanAmount', value)}
          suffix={disclosureContext.rowData.baseAsset}
          data-testid="loan-unit-loan-amount"
          touched={!!touched.unitLoanAmount}
          invalid={!!errors.unitLoanAmount}
        />
      </FormGroup>

      <FormGroup
        label={getFieldTitle(fixedRateLoanFieldDefinitions.loanPaymentCcy)}
        tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.loanPaymentCcy)}
        required={requiredFields.loanPaymentCcy}
        error={touched.loanPaymentCcy ? errors.loanPaymentCcy : undefined}
        mb="spacing4"
      >
        <SearchSelect<LoanPaymentCurrencyOption>
          options={loanPaymentCurrencyOptions}
          selection={loanPaymentCurrencySelection}
          onChange={handleLoanPaymentCurrencyChange}
          getLabel={getLoanPaymentCurrencyLabel}
          placeholder="Select Loan Payment Currency"
          showClear
          data-testid="fixed-rate-loan-payment-currency-select"
        />
      </FormGroup>

      <FormGroup
        label={getFieldTitle(fixedRateLoanFieldDefinitions.termType)}
        tooltip={appendTooltipWhenFeatureDisabled(
          getFieldTooltip(fixedRateLoanFieldDefinitions.termType),
          enableLoanOpenTermSupport,
          OPEN_TERM_SUPPORT_COMING_SOON
        )}
        required={requiredFields.termType}
        error={touched.termType ? errors.termType : undefined}
        mb="spacing4"
      >
        <SearchSelect<LoanTermTypeOption>
          options={LOAN_TERM_TYPE_OPTIONS}
          selection={termTypeSelection}
          onChange={handleTermTypeChange}
          getLabel={getTermTypeLabel}
          placeholder="Select Term Type"
          data-testid="fixed-rate-loan-term-type-select"
          disabled={!enableLoanOpenTermSupport || isBullet}
        />
      </FormGroup>

      {isTermTypeFixed ? (
        <FormGroup
          label={getFieldTitle(fixedRateLoanFieldDefinitions.term)}
          tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.term)}
          required={requiredFields.term}
          error={touched.term ? errors.term : undefined}
          mb="spacing4"
        >
          <DurationInput
            initialValue={formData.term ?? undefined}
            onChange={value => handleFieldChange('term', value)}
            placeholder="Enter custom term"
            dataTestId="fixed-rate-loan-term-input"
          />
        </FormGroup>
      ) : (
        <FormGroup
          label={getFieldTitle(fixedRateLoanFieldDefinitions.initialFixedTerm)}
          tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.initialFixedTerm)}
          required={requiredFields.initialTerm}
          error={touched.initialTerm ? errors.initialTerm : undefined}
          mb="spacing4"
        >
          <DurationInput
            initialValue={formData.initialTerm ?? undefined}
            onChange={value => handleFieldChange('initialTerm', value)}
            placeholder="Enter initial term"
            dataTestId="fixed-rate-loan-initial-term-input"
          />
        </FormGroup>
      )}

      <LoanPeriodFields
        formData={formData}
        handleFieldChange={handleFieldChange}
        errors={errors}
        touched={touched}
        requiredFields={requiredFields}
        isOpenTerm={!isTermTypeFixed}
        testIdPrefix="fixed-rate-loan"
        paymentPeriodField={fixedRateLoanFieldDefinitions.paymentPeriod}
        accrualPeriodField={fixedRateLoanFieldDefinitions.accrualPeriod}
        loanPaymentTypeField={fixedRateLoanFieldDefinitions.loanPaymentType}
      />

      <FormGroup
        label={getFieldTitle(fixedRateLoanFieldDefinitions.dayCountConvention)}
        tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.dayCountConvention)}
        required={requiredFields.dayCountConvention}
        error={touched.dayCountConvention ? errors.dayCountConvention : undefined}
        mb="spacing4"
      >
        <SearchSelect<DayCountConventionOption>
          options={dayCountConventionOptions}
          selection={dayCountConventionSelection}
          onChange={handleDayCountConventionChange}
          getLabel={getDayCountConventionLabel}
          placeholder="Select Day Count Convention"
          showClear
          data-testid="fixed-rate-loan-day-count-convention-select"
        />
      </FormGroup>
    </Stack>
  );
};
